import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { Action } from '@artosera/shared/permissions';
import { usePeut } from '../../auth/usePeut.js';
import type { VueDAigle } from '../../types.js';
import styles from './ATraiter.module.css';
import { LienConsole, Section } from './composants.js';
import commun from './commun.module.css';

/** Ce qui est urgent : une somme en retard. Le reste demande une saisie ou une décision. */
const TYPES_URGENTS = new Set(['creance_en_retard']);

/**
 * Le droit que la cible d'une ligne exige : la ligne reste affichée pour toute
 * l'exploitation, mais son lien ne l'est que pour qui peut ouvrir la cible
 * (une route sans droit renverrait à l'accueil). Par type de tâche d'abord,
 * par adresse pour un type que cette version ne connaît pas.
 */
const ACTION_PAR_TYPE: Record<string, Action> = {
  creance_en_retard: 'console:manage-receivables',
  montant_a_saisir: 'console:manage-subscriptions',
  couts_a_saisir: 'console:manage-costs',
  sans_contrat: 'console:read',
  essai_echu: 'console:read',
  releve_en_echec: 'console:manage-accounting',
};

function actionDuLien(type: string, lien: string): Action {
  const connue = ACTION_PAR_TYPE[type];
  if (connue) return connue;
  if (lien.startsWith('/finances')) return 'console:manage-subscriptions';
  if (lien.startsWith('/jalons')) return 'console:manage-milestones';
  if (lien.startsWith('/associes')) return 'console:manage-associates';
  return 'console:read';
}

/**
 * Le bloc « À traiter » : réservé à l'exploitation. L'API ne l'envoie pas à
 * l'associé (le champ est absent) ; le composant n'est rendu que s'il est là.
 */
export function ATraiter({
  lignes,
  numero,
}: {
  lignes: NonNullable<VueDAigle['aTraiter']>;
  numero: string;
}): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();

  return (
    <Section
      id="a-traiter"
      numero={numero}
      titre={t('vueDAigle.aTraiter.titre')}
      note={t('vueDAigle.aTraiter.note')}
      pastille={
        lignes.length > 0 ? (
          <span className={`${commun.pastille} ${commun.pastilleRouge}`}>{lignes.length}</span>
        ) : null
      }
      className={styles.panneau}
    >
      {lignes.length === 0 ? (
        <p className={commun.noteDiscrete}>{t('vueDAigle.aTraiter.vide')}</p>
      ) : (
        <ul className={styles.liste}>
          {lignes.map((ligne, rang) => (
            <li key={`${ligne.type}-${rang}`}>
              <span
                className={`${commun.point} ${TYPES_URGENTS.has(ligne.type) ? commun.pointRouge : commun.pointAmbre}`}
                aria-hidden="true"
              />
              <span className={styles.texte}>{ligne.libelle}</span>
              {peut(actionDuLien(ligne.type, ligne.lien)) ? (
                <LienConsole vers={ligne.lien}>
                  {t(`vueDAigle.aTraiter.action.${ligne.type}`, {
                    defaultValue: t('vueDAigle.aTraiter.action.defaut'),
                  })}
                </LienConsole>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
